/**
 * AsyncProductImage.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { useState, useEffect } from 'react';
import {
  getProductImageUrl,
  getProductImageUrlSync,
  productImageNeedsAsyncLookup,
} from '../utils/assetImageMapping';

const AsyncProductImage = ({
  product,
  className,
  alt,
  onError,
  loading = 'lazy',
  decoding = 'async',
  fetchPriority,
  ...props
}) => {
  const [imageUrl, setImageUrl] = useState(() => getProductImageUrlSync(product));
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const syncUrl = getProductImageUrlSync(product);
    setImageUrl(syncUrl);

    if (!productImageNeedsAsyncLookup(product)) {
      setIsLoading(false);
      return undefined;
    }

    let cancelled = false;
    setIsLoading(true);

    getProductImageUrl(product)
      .then((url) => {
        if (!cancelled) {
          setImageUrl(url);
          setIsLoading(false);
        }
      })
      .catch((error) => {
        console.error('Error loading asset image:', error);
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [product?.id, product?.image, product?.updated_at]);

  const handleError = (e) => {
    if (e.currentTarget.src.endsWith('/images/placeholder.jpg')) return;
    e.currentTarget.src = '/images/placeholder.jpg';
    if (onError) {
      onError(e);
    }
  };

  return (
    <img
      src={imageUrl}
      alt={alt || product?.name || 'Product image'}
      className={className}
      onError={handleError}
      loading={loading}
      decoding={decoding}
      fetchPriority={fetchPriority}
      style={{
        opacity: isLoading ? 0.85 : 1,
        transition: 'opacity 0.2s ease',
      }}
      {...props}
    />
  );
};

export default AsyncProductImage;
